import { createContext, useContext, useEffect, useState } from 'react'

// Three states, not two:
// - null      → visitor hasn't chosen yet, fork is shown in its resting state
// - 'business'  → "I need a website that converts" path
// - 'portfolio' → "I'm reviewing your work" path
// Persisted per-tab (sessionStorage, not localStorage) so a fresh visit
// always starts at the fork, but back/forward navigation and re-renders
// within the same session don't reset the choice.
const ViewModeContext = createContext(null)

// Query params that pre-select a view. Checked before sessionStorage so a
// shared recruiter link always wins, even if this browser/tab previously
// chose "business" on an earlier visit in the same session.
const REF_PARAM_MODES = {
  recruiter: 'portfolio',
  hiring: 'portfolio',
  client: 'business',
}

function getModeFromQuery() {
  if (typeof window === 'undefined') return null
  const ref = new URLSearchParams(window.location.search).get('ref')
  return ref ? REF_PARAM_MODES[ref] ?? null : null
}

function getInitialMode() {
  if (typeof window === 'undefined') return null
  const fromQuery = getModeFromQuery()
  if (fromQuery) return fromQuery
  const saved = window.sessionStorage.getItem('bwc-view-mode')
  return saved === 'business' || saved === 'portfolio' ? saved : null
}

export function ViewModeProvider({ children }) {
  const [mode, setModeState] = useState(getInitialMode)
  // True only when the mode came from a ?ref= link rather than a click on
  // the fork — lets JourneyFork show a slightly different, link-aware
  // status line ("You're viewing this via a recruiter link") instead of
  // implying the visitor clicked something they didn't.
  const [fromLink] = useState(() => Boolean(getModeFromQuery()))

  useEffect(() => {
    if (mode) window.sessionStorage.setItem('bwc-view-mode', mode)
  }, [mode])

  const setMode = (next) => setModeState(next)
  const resetMode = () => {
    setModeState(null)
    window.sessionStorage.removeItem('bwc-view-mode')
    // A manual "switch view" after landing via link should behave like a
    // fresh choice from then on, not keep re-asserting the link's mode.
    if (typeof window !== 'undefined' && window.history?.replaceState) {
      const url = new URL(window.location.href)
      if (url.searchParams.has('ref')) {
        url.searchParams.delete('ref')
        window.history.replaceState({}, '', url)
      }
    }
  }

  return (
    <ViewModeContext.Provider value={{ mode, setMode, resetMode, fromLink }}>
      {children}
    </ViewModeContext.Provider>
  )
}

export function useViewMode() {
  const ctx = useContext(ViewModeContext)
  if (!ctx) throw new Error('useViewMode must be used within ViewModeProvider')
  return ctx
}
